import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

function DevelopmentHub() {
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'ڈیولپمنٹ ہب - Galla Mandi Bills & Khata' : 'Development Hub - Galla Mandi Bills & Khata'}</title>
      </Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brand-header via-green-700 to-brand-header text-white py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-72 h-72 bg-green-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 right-0 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
          <div className={`container mx-auto px-4 text-center relative z-10 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in-up">
              {isUrdu ? 'ڈیولپمنٹ ہب' : 'Development Hub'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-medium">
              {isUrdu 
                ? 'تکنیکی ترقی اور جدت کے لیے مرکز' 
                : 'Center for technical development and innovation'}
            </p>
          </div>
        </section>

        {/* Development Services Section */}
        <section className={`py-20 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-brand-header mb-4">
                {isUrdu ? 'ہماری ترقی کی خدمات' : 'Our Development Services'}
              </h2>
              <p className="text-gray-600 text-lg">
                {isUrdu ? 'تکنیکی ترقی کے لیے مکمل حل' : 'Complete solutions for technical development'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: "💻",
                  title: isUrdu ? "سافٹ ویئر ڈویلپمنٹ" : "Software Development",
                  titleUrdu: "سافٹ ویئر ڈویلپمنٹ",
                  desc: isUrdu ? "مخصوص سافٹ ویئر حل" : "Custom software solutions",
                  descUrdu: "مخصوص سافٹ ویئر حل"
                },
                {
                  icon: "🌐",
                  title: isUrdu ? "ویب ڈویلپمنٹ" : "Web Development",
                  titleUrdu: "ویب ڈویلپمنٹ",
                  desc: isUrdu ? "جدید ویب ایپلیکیشنز" : "Modern web applications",
                  descUrdu: "جدید ویب ایپلیکیشنز"
                },
                {
                  icon: "📱",
                  title: isUrdu ? "موبائل ڈویلپمنٹ" : "Mobile Development",
                  titleUrdu: "موبائل ڈویلپمنٹ",
                  desc: isUrdu ? "iOS اور Android ایپس" : "iOS and Android apps",
                  descUrdu: "iOS اور Android ایپس"
                },
                {
                  icon: "🔧",
                  title: isUrdu ? "سسٹم انٹیگریشن" : "System Integration",
                  titleUrdu: "سسٹم انٹیگریشن",
                  desc: isUrdu ? "موجودہ سسٹمز کا انضمام" : "Integration of existing systems",
                  descUrdu: "موجودہ سسٹمز کا انضمام"
                },
                {
                  icon: "🔄",
                  title: isUrdu ? "اپ ڈیٹس" : "Updates",
                  titleUrdu: "اپ ڈیٹس",
                  desc: isUrdu ? "مسلسل بہتری اور اپ ڈیٹس" : "Continuous improvement and updates",
                  descUrdu: "مسلسل بہتری اور اپ ڈیٹس"
                },
                {
                  icon: "🛠️",
                  title: isUrdu ? "سپورٹ" : "Support",
                  titleUrdu: "سپورٹ",
                  desc: isUrdu ? "مکمل تکنیکی سپورٹ" : "Complete technical support",
                  descUrdu: "مکمل تکنیکی سپورٹ"
                }
              ].map((service, idx) => (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                >
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className={`text-2xl font-bold text-gray-900 mb-3 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? service.titleUrdu : service.title}
                  </h3>
                  <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? service.descUrdu : service.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-brand-header to-green-700 text-white">
          <div className={`container mx-auto px-4 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h2 className="text-4xl font-bold mb-4">
              {isUrdu ? 'ترقی شروع کریں' : 'Start Developing'}
            </h2>
            <p className="text-xl mb-8 text-white/90">
              {isUrdu ? 'اپنی تکنیکی ترقی کے لیے آج ہی رابطہ کریں' : 'Contact us today for your technical development'}
            </p>
            <Link
              to="/contact"
              className="bg-brand-button hover:bg-yellow-300 text-gray-900 font-bold px-8 py-4 rounded-xl transition transform hover:scale-105 hover:shadow-lg inline-block"
            >
              {isUrdu ? 'رابطہ کریں' : 'Contact Us'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}

export default DevelopmentHub;

